/**
 * Public preference card share page (Iter 9.40 Slice I.e - Kanban-card visual).
 * URL: /p/share_*  (or /share/*)
 *
 * Renders the EXACT same compact card the host sees in their Kanban
 * (3-image inspo strip, avatar + name + service, tag row, calendar date,
 * eye icon top-right). Tapping the eye icon opens the
 * UniversalPreferenceCardPopup with all sections expanded.
 *
 * Data: GET /api/share/{share_id}  → { share, target, owner, target.preferences_display }
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Eye, Heart, Sparkles, Loader2, Calendar as CalendarIcon, ImageIcon as ImgIcon, MoreVertical, Pencil, EyeOff, Share2, Trash2, Plus, UserPlus, X } from "lucide-react";
import { getCategoryPreset } from "@/lib/categoryConfig";
import { UniversalPreferenceCardPopup } from "@/components/UniversalPreferenceCardPopup";

const POST = (path, body) =>
  axios.post(`${API}${path}`, body || {}).catch(() => null);

/** Compact Kanban-style card with the 3-image inspo strip + eye → universal popup. */
function MenuItem({ icon: Icon, label, onClick, testid, tone = "default" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2.5 hover:bg-stone-50 ${tone === "danger" ? "text-red-600 hover:bg-red-50" : "text-stone-800"}`}
    >
      <Icon className={`w-3.5 h-3.5 ${tone === "danger" ? "text-red-500" : "text-stone-500"}`} />
      <span className="font-medium">{label}</span>
    </button>
  );
}

function ShareCard({ exp, ownerName, ownerImg, shareId, occasionDate }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const cat = String(exp?.category || exp?.pilot_category || "makeup").toLowerCase();
  const preset = getCategoryPreset(cat);

  const inspoUrls = (Array.isArray(exp?.inspo) ? exp.inspo : [])
    .map((i) => (typeof i === "string" ? i : i?.url || i?.image_url))
    .filter(Boolean);
  const inspoCells = [0, 1, 2].map((i) => inspoUrls[i] || null);

  const display = exp?.preferences_display || {};
  // Top tag row - pick up to 3 short labels from the answers (skip notes / long values)
  const topTags = [];
  Object.values(display).forEach((meta) => {
    if (!meta || topTags.length >= 3) return;
    const opts = meta.options || [];
    opts.forEach((o) => {
      if (topTags.length >= 3) return;
      const lbl = String(o.label || "").trim();
      if (lbl && lbl.length <= 18) topTags.push(lbl.toLowerCase());
    });
  });

  const linkedProducts = (exp?.linked_products_display || []).map((p) => ({
    brand: p.brand,
    name: p.name,
    shade: p.shade,
    image_url: p.image_url,
  }));

  const dateStr = (exp?.target_date || occasionDate)
    ? new Date(exp?.target_date || occasionDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })
    : "TBD";

  const onEye = () => {
    POST(`/share/${shareId}/event`, { event_type: "expand_detail", extra: { source: "card_eye" } });
    setPopupOpen(true);
  };

  return (
    <>
      <div
        className="bg-white rounded-2xl border border-stone-200 overflow-hidden shadow-sm w-full max-w-md mx-auto"
        data-testid={`share-card-${exp?.experience_id || "card"}`}
      >
        {/* 3-image inspo strip - exact Kanban look */}
        <div className="grid grid-cols-3 gap-px bg-stone-100">
          {inspoCells.map((src, i) => (
            <div
              key={i}
              className="aspect-square overflow-hidden flex items-center justify-center"
              style={{ backgroundColor: preset?.bgColor || "#f5f5f4" }}
            >
              {src ? (
                <img src={src} alt="inspo" className="w-full h-full object-cover" />
              ) : (
                <ImgIcon className="w-4 h-4 text-stone-300" />
              )}
            </div>
          ))}
        </div>

        {/* Body */}
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-full bg-stone-900 text-white flex items-center justify-center font-semibold shrink-0 overflow-hidden">
              {ownerImg ? (
                <img src={ownerImg} alt={ownerName} className="w-full h-full object-cover" />
              ) : (
                <span>{(ownerName || "C").slice(0, 1).toUpperCase()}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-bold text-stone-900 truncate text-base" data-testid="share-card-name">
                {ownerName || "Phygital user"}
              </p>
              <p className="text-sm text-stone-400 truncate" data-testid="share-card-service">
                {exp?.name || preset?.name || "Preference card"}
              </p>
            </div>
            <button
              onClick={onEye}
              className="p-1.5 rounded-full hover:bg-stone-100 transition-colors shrink-0"
              data-testid="share-card-eye"
              aria-label="View full preference card"
            >
              <Eye className="w-5 h-5 text-stone-400 hover:text-stone-700" />
            </button>
          </div>

          {/* Tag row */}
          {topTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3" data-testid="share-card-tags">
              {topTags.map((t, i) => (
                <span
                  key={i}
                  className="text-[12px] px-3 py-1 rounded-full bg-stone-100 text-stone-700 font-normal"
                >
                  {t}
                </span>
              ))}
            </div>
          )}

          {/* Date footer */}
          <div className="mt-4 flex items-center gap-1.5 text-xs text-stone-400">
            <CalendarIcon className="w-3.5 h-3.5" />
            {dateStr}
          </div>
        </div>
      </div>

      {/* Universal preference card popup - the same modal hosts see */}
      <UniversalPreferenceCardPopup
        open={popupOpen}
        onOpenChange={setPopupOpen}
        client={{
          name: ownerName,
          avatar_url: ownerImg,
          service: exp?.name || preset?.name,
          date: exp?.target_date || occasionDate,
        }}
        inspo={inspoUrls}
        tagLabels={topTags}
        linkedProducts={linkedProducts}
        finalLooks={Array.isArray(exp?.final_look_urls) ? exp.final_look_urls : []}
        categoryLightHex={preset?.bgColor || "#f5f5f4"}
        testIdPrefix="share-pref-card"
      />
    </>
  );
}

export default function PreferenceShareCardPage() {
  const { shareId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  // Iter 9.50+ R15 - role-aware 3-dot menu. The `?as=` query param lets us
  // demo the three states (owner/host/public) without auth - in production
  // the backend returns `viewer_role` on /share/{id}.
  const [menuOpen, setMenuOpen] = useState(false);
  const [visibilityModal, setVisibilityModal] = useState(false);
  const [hostUpdateModal, setHostUpdateModal] = useState(false);
  const viewerRole = searchParams.get("as") || data?.viewer_role || "public";

  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/share/${shareId}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) setErr(e?.response?.data?.detail || "Share not found"); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [shareId]);

  const onCtaPlanOwn = useCallback(() => {
    POST(`/share/${shareId}/event`, { event_type: "cta_plan_your_own_click" });
    navigate("/login?intent=plan");
  }, [shareId, navigate]);

  const onCtaProvider = useCallback(() => {
    POST(`/share/${shareId}/event`, { event_type: "cta_become_provider_click" });
    const cat = data?.target?.category || data?.target?.pilot_category || "";
    navigate(`/join${cat ? `?category=${cat}` : ""}`);
  }, [shareId, data, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-stone-50 p-6">
        <h1 className="text-xl font-semibold text-stone-800 mb-2">Share not found</h1>
        <p className="text-sm text-stone-500">This preference share link may have expired.</p>
      </div>
    );
  }

  const { share, target, owner } = data || {};
  const ownerName = owner?.display_name || [owner?.first_name, owner?.last_name].filter(Boolean).join(" ");
  const ownerImg = owner?.profile_image || owner?.profile_photo_url;
  const isOccasion = share?.share_type === "occasion";
  const advocateRate = share?.commission_rate;
  const advocateName = share?.advocate_name || data?.advocate?.display_name;

  return (
    <div className="min-h-screen bg-stone-50" data-testid="preference-share-page">
      {/* Slim header bar with role-aware 3-dot menu - Iter 9.50+ R15 */}
      <div className="bg-white border-b border-stone-200 relative">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <div className="w-7" />
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-stone-400" />
            <span className="text-xs uppercase tracking-wider text-stone-400 font-medium">
              {isOccasion ? "Occasion - shared with you" : "Preference card - shared with you"}
            </span>
          </div>
          {/* 3-dot menu - actions vary by viewer role */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-stone-100"
              data-testid="share-card-3dot-menu-trigger"
              aria-label="Menu"
            >
              <MoreVertical className="w-4 h-4 text-stone-500" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-full mt-1 w-56 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden z-30"
                data-testid={`share-card-3dot-menu-${viewerRole}`}
              >
                <div className="px-3 py-2 border-b border-stone-100 bg-stone-50">
                  <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold">
                    {viewerRole === "owner" ? "You own this card" : viewerRole === "host" ? "You're the host" : "Public viewer"}
                  </p>
                </div>
                {viewerRole === "owner" && (
                  <>
                    <MenuItem icon={EyeOff} label="Control visibility" testid="menu-control-visibility" onClick={() => { setVisibilityModal(true); setMenuOpen(false); }} />
                    <MenuItem icon={Pencil} label="Edit card" testid="menu-edit-card" onClick={() => { setMenuOpen(false); navigate("/me?openCardEdit=" + shareId); }} />
                    <MenuItem icon={Share2} label="Share again" testid="menu-share-again" onClick={() => { setMenuOpen(false); navigator.clipboard?.writeText(window.location.href); }} />
                    <MenuItem icon={Trash2} label="Delete share link" tone="danger" testid="menu-delete-card" onClick={() => setMenuOpen(false)} />
                  </>
                )}
                {viewerRole === "host" && (
                  <>
                    <MenuItem icon={Pencil} label="Update preference card" testid="menu-host-update-card" onClick={() => { setHostUpdateModal(true); setMenuOpen(false); }} />
                    <MenuItem icon={Plus} label="Add to my kanban" testid="menu-host-add-kanban" onClick={() => setMenuOpen(false)} />
                    <MenuItem icon={UserPlus} label="Add this client to my directory" testid="menu-host-add-client" onClick={() => setMenuOpen(false)} />
                    <MenuItem icon={Share2} label="Copy share link" testid="menu-host-share" onClick={() => { setMenuOpen(false); navigator.clipboard?.writeText(window.location.href); }} />
                  </>
                )}
                {viewerRole === "public" && (
                  <>
                    <MenuItem icon={Share2} label="Copy share link" testid="menu-public-share" onClick={() => { setMenuOpen(false); navigator.clipboard?.writeText(window.location.href); }} />
                    <MenuItem icon={Heart} label="Save to my profile" testid="menu-public-save" onClick={() => { setMenuOpen(false); navigate("/login?intent=save_card&share=" + shareId); }} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Visibility modal (owner role) - Iter 9.50+ R15 */}
      {visibilityModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setVisibilityModal(false)} data-testid="visibility-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-stone-900">Preference card visibility</h3>
              <button onClick={() => setVisibilityModal(false)} className="text-stone-400 hover:text-stone-700" data-testid="visibility-modal-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-stone-500">Choose who can see this card and what they see.</p>
            <div className="space-y-2">
              {[
                { v: "public", title: "Public", subtitle: "Anyone with the link" },
                { v: "host_only", title: "Host only", subtitle: "Only providers you've linked" },
                { v: "private", title: "Private", subtitle: "Just you in your profile" },
              ].map((opt) => (
                <button key={opt.v} className="w-full text-left px-3 py-2.5 rounded-xl border border-stone-200 hover:border-stone-400" data-testid={`visibility-option-${opt.v}`}>
                  <p className="text-sm font-semibold text-stone-900">{opt.title}</p>
                  <p className="text-[11px] text-stone-500">{opt.subtitle}</p>
                </button>
              ))}
            </div>
            <div className="pt-2 border-t">
              <p className="text-[10px] uppercase tracking-wider text-stone-500 font-bold mb-2">What&apos;s visible on the card</p>
              <div className="space-y-1.5">
                {["Preferences", "Linked products", "Inspiration images", "Service + date", "Budget"].map((f) => (
                  <label key={f} className="flex items-center gap-2 text-xs text-stone-700">
                    <input type="checkbox" defaultChecked className="accent-stone-900" data-testid={`visibility-field-${f.toLowerCase().replace(/ /g,'-')}`} />
                    {f}
                  </label>
                ))}
              </div>
            </div>
            <Button onClick={() => setVisibilityModal(false)} className="w-full h-10 bg-stone-900 text-white" data-testid="visibility-save-btn">Save changes</Button>
          </div>
        </div>
      )}

      {/* Host update modal */}
      {hostUpdateModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setHostUpdateModal(false)} data-testid="host-update-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full mx-4 p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-stone-900">Update preference card</h3>
              <button onClick={() => setHostUpdateModal(false)} className="text-stone-400" data-testid="host-update-close"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-stone-500">Add a note for the client or attach your own products / inspo. Your changes stay scoped to your dashboard.</p>
            <textarea placeholder="Note for this client (only you see this)..." rows={4} className="w-full px-3 py-2 border border-stone-200 rounded-lg text-xs resize-none" data-testid="host-update-notes" />
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setHostUpdateModal(false)} className="flex-1 h-10 text-xs" data-testid="host-update-cancel">Cancel</Button>
              <Button onClick={() => setHostUpdateModal(false)} className="flex-1 h-10 bg-stone-900 text-white text-xs" data-testid="host-update-save">Save to my kanban</Button>
            </div>
          </div>
        </div>
      )}

      {/* Body - centered card + CTAs immediately below (Iter 9.40 Slice I.f) */}
      <div className="min-h-[calc(100vh-56px)] flex flex-col items-center justify-start px-4 py-8 sm:py-12">
        <div className="w-full max-w-md flex flex-col items-center gap-5">
          {isOccasion ? (
            <>
              <h1 className="text-xl font-bold text-stone-900 text-center" data-testid="occasion-title">
                {target?.name || share?.display_name}
              </h1>
              {(target?.linked || []).map((exp) => (
                <ShareCard
                  key={exp.experience_id}
                  exp={exp}
                  ownerName={ownerName}
                  ownerImg={ownerImg}
                  shareId={shareId}
                  occasionDate={target?.date}
                />
              ))}
            </>
          ) : (
            target && (
              <ShareCard
                exp={target}
                ownerName={ownerName}
                ownerImg={ownerImg}
                shareId={shareId}
              />
            )
          )}

          {advocateRate > 0 && (
            <div className="w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800 flex items-center justify-center gap-2 text-center" data-testid="advocate-commission">
              <Heart className="w-3.5 h-3.5" />
              <span>
                {advocateName ? <strong>{advocateName}</strong> : "Your advocate"} earns {(advocateRate * 100).toFixed(0)}% on bookings via this link.
              </span>
            </div>
          )}

          {/* CTAs - sit right below the card, centered text - Iter 9.50+ R15
              Three variants:
              - Host viewer: pops a host-specific quick action set
              - Owner viewer: just a "Manage in my profile" link (their card)
              - Public viewer: Plan your own + Join Phygital + Become a provider */}
          <div className="w-full space-y-2 mt-2">
            {viewerRole === "owner" && (
              <Button
                onClick={() => navigate("/me")}
                className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white font-semibold justify-center"
                data-testid="cta-manage-in-profile"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Manage in my profile
              </Button>
            )}
            {viewerRole === "host" && (
              <>
                <Button
                  onClick={() => setHostUpdateModal(true)}
                  className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white font-semibold justify-center"
                  data-testid="cta-host-add-to-kanban"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add to my kanban
                </Button>
                <Button
                  onClick={() => navigate("/host/clients/import?from=" + shareId)}
                  variant="outline"
                  className="w-full h-11 font-semibold justify-center"
                  data-testid="cta-host-import-client"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Import as client
                </Button>
              </>
            )}
            {viewerRole === "public" && (
              <>
                <Button
                  onClick={onCtaPlanOwn}
                  className="w-full h-11 bg-stone-900 hover:bg-stone-800 text-white font-semibold justify-center"
                  data-testid="cta-plan-your-own"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Plan your own experience
                </Button>
                <Button
                  onClick={() => navigate("/signup?intent=join&share=" + shareId)}
                  className="w-full h-11 bg-purple-600 hover:bg-purple-700 text-white font-semibold justify-center"
                  data-testid="cta-join-phygital"
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Join Phygital
                </Button>
                <Button
                  onClick={onCtaProvider}
                  variant="outline"
                  className="w-full h-11 font-semibold justify-center"
                  data-testid="cta-become-provider"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Become a provider on Phygital
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
